import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck,
  MapPin,
  Building,
  Home as HomeIcon,
  Loader2,
  Check,
  Search,
  Zap,
  AlertCircle,
} from "lucide-react";
import Layout from "../components/Layout";
import PageBanner from "../components/PageBanner";
import { api } from "../lib/api";
import { enrichWithRentCast } from "../lib/rentcastEnrich";
import { trackGeoPrefillUsed, trackAddressSearch } from "../lib/ga";

const SERVICE_LABELS = {
  apartments: "home.service.apartments",
  residential: "home.service.residential",
  commercial: "home.service.commercial",
};

const STATUS_TO_KEY = {
  Active: "statusActive",
  Inactive: "statusInactive",
  "De-Energized": "statusDeEnergized",
};

const PREMISE_TO_KEY = {
  Residential: "premiseResidential",
  "Small Non-Residential": "premiseSmallCommercial",
  "Large Non-Residential": "premiseLargeCommercial",
};

function statusColor(status) {
  if (status === "Active") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (status === "Inactive") return "bg-amber-100 text-amber-700 border-amber-200";
  if (status === "De-Energized") return "bg-rose-100 text-rose-700 border-rose-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

export default function Address() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [address, setAddress] = useState(
    // Accept ?address= from city landing search bars (Feb 2026) so the
    // user's typed text carries through. Falls back to localStorage so a
    // returning visitor sees what they typed last session.
    params.get("address") || localStorage.getItem("velcor_service_address") || ""
  );
  const [unit, setUnit] = useState(localStorage.getItem("velcor_unit") || "");
  const [city, setCity] = useState(
    // Accept ?city= from city landing CTAs first, then localStorage, then default.
    params.get("city") || localStorage.getItem("velcor_city") || "Houston"
  );
  const [manualOpen, setManualOpen] = useState(false);
  const [error, setError] = useState("");

  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);

  const debounceRef = useRef();

  const [zipState, setZipState] = useState(
    params.get("zip") || localStorage.getItem("velcor_zip") || ""
  );
  // Backward-compat alias: rest of the component reads `zip` as before.
  const zip = zipState;
  const type = params.get("type") || localStorage.getItem("velcor_service_type") || "residential";
  const isCommercial = type === "commercial";
  // If the user landed here from /residential | /commercial | /apartments
  // because they had no ESIID yet, we read the requested `next` path so we
  // can bounce them back to the SAME plans page once the address is captured
  // (instead of always defaulting to /residential).
  const nextParam = params.get("next") || "";
  const reasonParam = params.get("reason") || "";

  // Geo-prefill state: shows a subtle pill when we auto-filled the ZIP
  // from the visitor's IP (so they understand the field wasn't magic).
  const [geoPrefill, setGeoPrefill] = useState(null);

  useEffect(() => {
    if (zip) localStorage.setItem("velcor_zip", zip);
    if (type) localStorage.setItem("velcor_service_type", type);
    // Persist city from URL params (city landing CTAs) so it survives reload.
    const cityFromUrl = params.get("city");
    if (cityFromUrl) localStorage.setItem("velcor_city", cityFromUrl);
  }, [zip, type, params]);

  // ── Auto-search on arrival when ?address= is passed (Feb 2026) ────────
  // City landing search bars navigate here with the user's typed address
  // pre-populated. We immediately fire the ESIID lookup so the candidates
  // dropdown appears on first paint — visitor sees their address resolved
  // (or candidates to pick) without retyping. This is THE conversion
  // smoothing: typed address from /electricidad-houston bar → instant
  // results on /address with the same address already searched.
  useEffect(() => {
    const incoming = params.get("address");
    if (!incoming || !incoming.trim()) return;
    if (incoming.trim().length < 3) return;
    // Defer one tick to let the rest of the state settle (zip/type from
    // URL params, geo-prefill, etc.) before firing the lookup.
    const t = setTimeout(() => triggerLookup(incoming), 80);
    return () => clearTimeout(t);
    // Intentionally run once on mount — re-running on every params change
    // would interfere with the user editing the address manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── IP geolocation pre-fill (Feb 2026) ────────────────────────────────
  // Visitors coming from Google Ads links don't carry a ZIP. We do a
  // best-effort backend lookup (ipapi via /api/public/geo-prefill) and
  // pre-fill the ZIP only when the visitor is in Texas. Zero clicks on
  // hit; silent no-op on miss. Privacy: only the resolved city/ZIP is
  // returned to the client — no raw IP, no tracking.
  useEffect(() => {
    if (zipState) return;  // user already has a ZIP — don't override
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/geo-prefill");
        if (cancelled) return;
        if (data?.ok && data.zip) {
          setZipState(data.zip);
          if (data.city) {
            setCity(data.city);
            localStorage.setItem("velcor_city", data.city);
          }
          localStorage.setItem("velcor_zip", data.zip);
          setGeoPrefill({ zip: data.zip, city: data.city });
        }
      } catch {
        // Silent fail — manual entry still works.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerLookup = async (val) => {
    const v = (val ?? address).trim();
    if (v.length < 3) return;
    setSearching(true);
    setSearched(false);
    setSelected(null);
    // Funnel step 1/5 — visitor reached /address and is searching.
    trackAddressSearch({ source: "address_page", hasZip: !!zip, city: params.get("city") || "" });
    try {
      // Don't pin the request to a specific ZIP / city — the operator may
      // type an address that lives in a different ZIP than the one captured
      // by GetStartedZipBanner. Free-text address lookup mirrors the public
      // hero behaviour and produces consistent autocomplete.
      const { data } = await api.post("/comparepower/esiid_lookup", {
        address: v,
        city: "",
        state: "TX",
        zip: "",
      });
      let list = data?.candidates || [];
      // Filter by service type: residential/apartments → keep Residential matches preferred, commercial → Non-Residential
      if (!isCommercial) {
        const resOnly = list.filter((c) => (c.premise_type || "").toLowerCase().includes("residential") && !(c.premise_type || "").toLowerCase().includes("non"));
        if (resOnly.length > 0) list = resOnly.concat(list.filter((c) => !resOnly.includes(c)));
      } else {
        const nonRes = list.filter((c) => (c.premise_type || "").toLowerCase().includes("non-residential"));
        if (nonRes.length > 0) list = nonRes.concat(list.filter((c) => !nonRes.includes(c)));
      }
      setCandidates(list);
    } catch (e) {
      setCandidates([]);
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  const onAddressChange = (val) => {
    setAddress(val);
    if (error) setError("");
    setSelected(null);
    setSearched(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length >= 3) {
      // Aggressive autocomplete: 220ms — feels instant, matches AddressHero.
      debounceRef.current = setTimeout(() => triggerLookup(val), 220);
    } else {
      setCandidates([]);
    }
  };

  const handlePick = (c) => {
    // Auto-navigate: picking a candidate IS the confirmation. No "Continuar"
    // button needed below — the user expects ComparePower-style instant flow.
    const finalAddress = c.address;
    const finalZip = c.zip || zip || "";
    localStorage.setItem("velcor_service_address", finalAddress);
    localStorage.setItem("velcor_esiid", c.esiid || "");
    localStorage.setItem("velcor_utility", c.utility_name || "");
    // Persist switch hold so it travels through /enroll into the lead doc;
    // backend stores it under `switch_hold_indicator` ("Y"/"N") and the
    // CRM drawer surfaces it as a critical banner for the activation agent.
    const shi = c.switch_hold_indicator
      || (c.switch_hold === true ? "Y" : c.switch_hold === false ? "N" : "");
    localStorage.setItem("velcor_switch_hold_indicator", (shi || "").toString().toUpperCase());
    if (c.city) localStorage.setItem("velcor_city", c.city);
    if (finalZip) localStorage.setItem("velcor_zip", finalZip);

    const q = new URLSearchParams();
    if (finalZip) q.set("zip", finalZip);
    q.set("type", type);
    q.set("address", finalAddress);
    if (unit) q.set("unit", unit);
    if (c.city) q.set("city", c.city);
    if (c.esiid) q.set("esiid", c.esiid);
    if (c.utility_name) q.set("utility", c.utility_name);

    // Honor ?next= so the user returns to the plans page they came from.
    // The plan-type segment is whatever the user originally requested
    // (apartments/residential/commercial); we keep `type` in the query for
    // legacy callers that still read it. /enroll also supported so visitors
    // who land directly on /enroll without an ESIID get bounced back here
    // and then forward to /enroll with all the address data.
    const dest = nextParam && /^\/(apartments|residential|commercial|enroll)$/.test(nextParam)
      ? nextParam
      : `/${type}`;
    // GA4: log conversion when this submission used the IP-geo prefill,
    // so we can A/B-measure the lift the feature produces.
    if (geoPrefill) {
      try { trackGeoPrefillUsed({ zip: geoPrefill.zip, city: geoPrefill.city }); } catch { /* noop */ }
    }
    navigate(`${dest}?${q.toString()}`);
  };

  const handleContinue = async (e) => {
    e?.preventDefault?.();
    const finalAddress = (selected?.address || address).trim();
    if (!finalAddress) {
      setError(t("address.addressRequired"));
      return;
    }
    localStorage.setItem("velcor_service_address", finalAddress);
    if (unit) localStorage.setItem("velcor_unit", unit);
    if (city) localStorage.setItem("velcor_city", city);

    // RentCast enrichment (shared helper — same call used by AddressHero
    // and the CityAddressBar so all entry points produce the same routing).
    const { routedType, profileId } = await enrichWithRentCast({
      address: finalAddress,
      city: city || "",
      zip: zip || "",
      type,
    });

    const q = new URLSearchParams();
    if (zip) q.set("zip", zip);
    q.set("type", routedType);
    q.set("address", finalAddress);
    if (unit) q.set("unit", unit);
    if (city) q.set("city", city);
    if (selected?.esiid) q.set("esiid", selected.esiid);
    if (selected?.utility_name) q.set("utility", selected.utility_name);
    if (profileId) q.set("profile", profileId);

    // Same ?next= guard as pickCandidate — keep the user on the plan tier
    // they actually wanted to see, but honor RentCast's tier auto-switch
    // if it disagrees (commercial vs residential). /enroll is allowed as
    // a destination so the same redirect-back-to-original-path works for
    // the enrollment flow.
    const dest = nextParam === "/enroll"
      ? "/enroll"
      : `/${routedType}`;
    // GA4: same geo-prefill conversion marker as the candidate path.
    if (geoPrefill) {
      try { trackGeoPrefillUsed({ zip: geoPrefill.zip, city: geoPrefill.city }); } catch { /* noop */ }
    }
    navigate(`${dest}?${q.toString()}`);
  };

  const serviceLabel = t(SERVICE_LABELS[type] || SERVICE_LABELS.residential);

  return (
    <Layout ribbon minimalFooter>
      <Helmet>
        <title>
          {t("address.title1")} {t("address.titleAccent")} — Velcor Energy
        </title>
      </Helmet>

      {/* Page-wide fixed background — covers the ENTIRE viewport (not just the
          hero section) so there's NEVER a white strip between the section
          bottom and the footer, regardless of viewport height. The footer's
          own navy bg covers this when scrolled into view. */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10"
        style={{ background: "#0a1229" }}
        data-testid="address-bg-fixed"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/hero-homes.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,18,41,0.62) 0%, rgba(10,18,41,0.38) 45%, rgba(10,18,41,0.18) 75%, rgba(10,18,41,0.05) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none mix-blend-overlay"
          style={{
            opacity: 0.05,
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />
      </div>

      {/* Hero content sits on top of the fixed background (no own bg layers
          needed — the fixed div above provides them). */}
      <section
        className="relative"
        data-testid="address-hero"
      >
        <div className="relative z-10 max-w-[980px] mx-auto px-5 pt-14 pb-8 md:pt-20 md:pb-10 text-center text-white">
          <div
            className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] mb-3"
            style={{ color: "#bce5c6" }}
            data-testid="address-banner-eyebrow"
          >
            {t("address.badge")}
          </div>
          <h1
            className="font-black text-white text-[30px] md:text-[44px] leading-[1.05] tracking-tight max-w-[820px] mx-auto"
            style={{
              fontFamily: '"Fraunces", "Source Serif Pro", Georgia, serif',
              textShadow: "0 2px 18px rgba(0,0,0,0.35)",
            }}
            data-testid="address-banner-title"
          >
            {t("address.title1")}{" "}
            <span style={{ color: "#bce5c6" }}>{t("address.titleAccent")}</span>{" "}
            {t("address.title2")}
          </h1>
          <p
            className="text-white/90 text-[14px] md:text-[16px] mt-4 max-w-[640px] mx-auto leading-relaxed"
            style={{ textShadow: "0 1px 10px rgba(0,0,0,0.35)" }}
            data-testid="address-banner-subtitle"
          >
            {t("address.lead")}
          </p>
        </div>

        {/* Form card overlaps slightly into the hero on desktop so it feels connected.
            Max-width 980 (vs 1180) keeps the card prominent but not oversized. */}
        <div
          className="relative z-10 max-w-[980px] mx-auto px-5 pb-20 md:pb-24"
          data-testid="address-page"
        >
          {(reasonParam === "plans" || reasonParam === "enroll") && (
            <div
              className="max-w-[640px] mx-auto mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
              data-testid="address-from-plans-banner"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={2.4} />
              <div className="text-[13px] leading-snug text-amber-900">
                <strong className="font-extrabold">
                  {reasonParam === "enroll"
                    ? t("address.banner.enrollStrong")
                    : t("address.banner.plansStrong")}
                </strong>{" "}
                {reasonParam === "enroll"
                  ? t("address.banner.enrollBody")
                  : t("address.banner.plansBody")}
              </div>
            </div>
          )}

          <div
          className="glass-strong rounded-[28px] p-5 md:p-7 shadow-[0_24px_70px_rgba(15,23,42,0.08)]"
          data-testid="address-card"
        >
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5 mb-6 text-[13px] font-extrabold text-slate-700">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-velcor-green" />
              {t("address.zip")}: <b data-testid="address-zip">{zip || "—"}</b>
              {geoPrefill && (
                <span
                  className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"
                  data-testid="address-geo-prefill-pill"
                  title={`${t("address.geoPrefill.title")}${geoPrefill.city ? " · " + geoPrefill.city : ""}`}
                >
                  {t("address.geoPrefill.pill")}
                </span>
              )}
            </span>
            <span className="opacity-40">•</span>
            <span className="inline-flex items-center gap-1.5">
              <Building className="w-4 h-4 text-velcor-green" />
              {t("address.state")}
            </span>
            <span className="opacity-40">•</span>
            <span className="inline-flex items-center gap-1.5">
              <HomeIcon className="w-4 h-4 text-velcor-green" />
              {t("address.service")}: <b data-testid="address-service">{serviceLabel}</b>
            </span>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              triggerLookup();
            }}
            className="rounded-[22px] md:rounded-full bg-white border border-slate-200 grid md:flex md:items-center gap-3 p-2.5 md:p-2 md:pl-5"
            style={{ boxShadow: "0 16px 40px rgba(15,23,42,.12)" }}
          >
            <span
              className="hidden md:grid place-items-center rounded-full shrink-0"
              style={{ width: 44, height: 44, background: "rgba(47,155,67,0.12)", color: "#2f7a3a" }}
            >
              <MapPin className="w-5 h-5" />
            </span>
            <input
              type="text"
              value={address}
              onChange={(e) => onAddressChange(e.target.value)}
              placeholder={t("address.addressPlaceholder")}
              className="flex-1 min-w-0 bg-transparent outline-none px-2 py-2.5 md:py-0 text-[15px] font-bold placeholder:text-slate-400 min-h-[48px]"
              autoComplete="street-address"
              data-testid="address-input"
            />
            <button
              type="submit"
              className="btn-green min-h-[46px] px-5 text-[14px]"
              data-testid="address-search"
              disabled={searching}
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("address.searching")}
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  {t("address.verifyEsiid")}
                </>
              )}
            </button>
          </form>

          {error && (
            <div className="mt-3 inline-flex items-center gap-2 text-rose-600 text-[13px] font-bold" data-testid="address-error">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Candidates list */}
          {searched && candidates.length === 0 && !searching && (
            <div className="mt-5 p-4 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 text-[13.5px] font-bold flex items-start gap-3" data-testid="esiid-no-matches">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              {t("address.noMatches")}
            </div>
          )}

          {candidates.length > 0 && (
            <div className="mt-6 animate-slide-down" data-testid="esiid-results">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-black text-[15px] text-velcor-ink">
                  <Zap className="inline w-4 h-4 text-velcor-green mr-1.5" />
                  {t("address.matchesFound")}{" "}
                  <span className="text-slate-400 font-bold text-[12.5px]">({candidates.length})</span>
                </h3>
              </div>
              <p className="text-slate-500 text-[12.5px] mb-4">{t("address.matchesHint")}</p>

              <div className="grid gap-2.5">
                {candidates.map((c) => {
                  const isSelected = selected?.esiid === c.esiid;
                  return (
                    <button
                      key={c.esiid}
                      type="button"
                      onClick={() => handlePick(c)}
                      data-testid={`esiid-candidate-${c.esiid}`}
                      className={`text-left rounded-2xl px-4 py-3.5 border-2 transition-all bg-white ${
                        isSelected
                          ? "border-velcor-green shadow-[0_18px_36px_rgba(47,122,58,0.14)] ring-1 ring-velcor-green/20"
                          : "border-slate-200 hover:border-velcor-green/40 hover:shadow-md"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-black text-velcor-ink text-[15px] mb-1 flex items-center gap-2 flex-wrap">
                            <MapPin className="w-4 h-4 text-velcor-green" />
                            <span className="truncate">{c.address}</span>
                            {isSelected && (
                              <span className="pill bg-velcor-green text-white text-[10.5px] font-extrabold px-2 py-0.5">
                                <Check className="w-3 h-3 mr-1" />
                                {t("address.selected")}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-[11.5px] font-extrabold">
                            <span className="pill px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200">
                              <span className="opacity-60 mr-1">{t("address.utilityBadge")}:</span>
                              {c.utility_name}
                            </span>
                            <span className="pill px-2.5 py-1 bg-velcor-green/10 text-velcor-green border border-velcor-green/20">
                              {t(`address.${PREMISE_TO_KEY[c.premise_type] || "premiseResidential"}`)}
                            </span>
                            <span className={`pill px-2.5 py-1 border ${statusColor(c.status)}`}>
                              {t(`address.${STATUS_TO_KEY[c.status] || "statusActive"}`)}
                            </span>
                          </div>
                          <div className="mt-2 text-[11.5px] text-slate-500 font-mono tracking-tight break-all">
                            {t("address.esiidBadge")}: <b className="text-velcor-ink">{c.esiid}</b>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Manual fallback */}
          <button
            type="button"
            onClick={() => setManualOpen((v) => !v)}
            className="mt-5 text-[13.5px] font-extrabold text-velcor-green underline underline-offset-4"
            data-testid="address-manual-toggle"
          >
            {t("address.manualToggle")}
          </button>

          {manualOpen && (
            <div className="mt-4 grid md:grid-cols-2 gap-3 animate-slide-down" data-testid="address-manual">
              <div className="md:col-span-2">
                <label className="block text-[12px] font-black text-slate-700 mb-1.5">
                  {t("address.addressLabel")}
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white outline-none focus:border-velcor-green text-[14px]"
                />
              </div>
              <div>
                <label className="block text-[12px] font-black text-slate-700 mb-1.5">{t("address.unitLabel")}</label>
                <input
                  type="text"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white outline-none focus:border-velcor-green text-[14px]"
                />
              </div>
              <div>
                <label className="block text-[12px] font-black text-slate-700 mb-1.5">{t("address.cityLabel")}</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white outline-none focus:border-velcor-green text-[14px]"
                />
              </div>
            </div>
          )}
        </div>
        </div>
      </section>
    </Layout>
  );
}
